/**
 * Centralized type mapping utilities for converting between different type representations
 */

// Frontend display type to backend field type mapping
export const FRONTEND_TO_BACKEND_TYPE_MAP: Record<string, string> = {
  'string': 'string',
  'text': 'text',
  'number': 'float',  // Default number to float
  'integer': 'integer',
  'float': 'float',
  'boolean': 'boolean',
  'date': 'date',
  'datetime': 'datetime',
  'json': 'json',
  'object': 'json',  // AI often returns 'object' instead of 'json'
  'uuid': 'uuid',
  'array': 'array',
  'reference': 'reference'
};

// Backend field type to Python type mapping
export const BACKEND_TO_PYTHON_TYPE_MAP: Record<string, string> = {
  'string': 'str',
  'text': 'str',
  'integer': 'int',
  'float': 'float',
  'boolean': 'bool',
  'date': 'date',
  'datetime': 'datetime',
  'json': 'dict',
  'uuid': 'UUID',
  'array': 'list',
  'reference': 'UUID'
};

// AI response type to backend type mapping
export const AI_TO_BACKEND_TYPE_MAP: Record<string, string> = {
  'string': 'string',
  'text': 'text',
  'number': 'float',  // AI often says "number" - default to float
  'integer': 'integer',
  'float': 'float',
  'boolean': 'boolean',
  'bool': 'boolean',  // AI might use 'bool'
  'date': 'date',
  'datetime': 'datetime',
  'object': 'json',   // AI often says "object" for complex types
  'json': 'json',
  'uuid': 'uuid',
  'array': 'array',
  'list': 'array',    // AI might use 'list'
  'reference': 'reference'
};

/**
 * Convert frontend/display type to backend API type
 */
export function mapFrontendToBackendType(frontendType: string): string {
  return FRONTEND_TO_BACKEND_TYPE_MAP[frontendType] || frontendType;
}

/**
 * Convert backend field type to Python type for Pydantic
 */
export function mapBackendToPythonType(backendType: string): string {
  return BACKEND_TO_PYTHON_TYPE_MAP[backendType] || 'str';
}

/**
 * Convert AI response type to backend type
 */
export function mapAIToBackendType(aiType: string): string {
  return AI_TO_BACKEND_TYPE_MAP[aiType.toLowerCase()] || aiType;
}

/**
 * Get Python type for array items
 */
export function getPythonArrayItemType(itemType?: string): string {
  if (!itemType) return 'str';
  return mapBackendToPythonType(itemType);
}

/**
 * Determine if a type should be treated as numeric
 */
export function isNumericType(type: string): boolean {
  const numericTypes = ['number', 'integer', 'float', 'int', 'double'];
  return numericTypes.includes(type.toLowerCase());
}

/**
 * Convert a field type from any source to backend format
 */
export function normalizeFieldType(type: string, source: 'frontend' | 'ai' | 'backend' = 'frontend'): string {
  switch (source) {
    case 'ai':
      return mapAIToBackendType(type);
    case 'frontend':
      return mapFrontendToBackendType(type);
    case 'backend':
    default:
      return type;
  }
}

/**
 * Map fields for backend API submission
 * Converts dot notation fields into proper nested structure with nested_fields
 */
export function convertFieldsForBackend(fields: any[]): any[] {
  const result: any[] = [];
  const nestedGroups: Map<string, any[]> = new Map();
  
  fields.forEach(field => {
    // Get the field name (handle both field_name and name)
    const fieldName = field.field_name || field.name || '';
    
    if (fieldName.includes('.')) {
      // This is a nested field (e.g., macros.calories)
      const parts = fieldName.split('.');
      const parentName = parts[0];
      const childName = parts.slice(1).join('.');
      
      if (!nestedGroups.has(parentName)) {
        nestedGroups.set(parentName, []);
      }
      
      nestedGroups.get(parentName)!.push({
        name: childName,
        display_name: childName.split('_').map((w: string) => 
          w.charAt(0).toUpperCase() + w.slice(1)
        ).join(' '), // Convert child_name to "Child Name" format
        field_type: normalizeFieldType(field.field_type || field.type, 'ai'),
        is_required: field.is_required !== undefined ? !!field.is_required : !!field.required,
        is_array: !!field.is_array,
        description: field.description || field.help_text || '',
        help_text: field.description || field.help_text || '',
        ...(field.array_item_type && { array_item_type: normalizeFieldType(field.array_item_type, 'ai') }),
        ...(field.reference_object_id && { reference_object_id: field.reference_object_id }),
        ...(field.allowed_values && { allowed_values: field.allowed_values }),
        ...(field.default_value !== undefined && { default_value: field.default_value }),
        ...(field.placeholder && { placeholder: field.placeholder })
      });
    } else if (field.field_type === 'json' && field.fields && field.fields.length > 0) {
      // Already properly nested field
      result.push({
        name: fieldName,
        display_name: field.display_name || fieldName,
        field_type: 'json',
        is_required: field.is_required !== undefined ? !!field.is_required : !!field.required,
        is_array: !!field.is_array,
        description: field.description || field.help_text || '',
        help_text: field.description || field.help_text || '',
        nested_fields: field.fields.map((f: any) => ({
          name: f.name || f.field_name,
          display_name: f.display_name || f.name || f.field_name,
          field_type: normalizeFieldType(f.field_type || f.type, 'ai'),
          is_required: f.is_required !== undefined ? !!f.is_required : !!f.required,
          is_array: !!f.is_array,
          description: f.description || f.help_text || '',
          help_text: f.description || f.help_text || '',
          ...(f.array_item_type && { array_item_type: normalizeFieldType(f.array_item_type, 'ai') }),
          ...(f.reference_object_id && { reference_object_id: f.reference_object_id }),
          ...(f.allowed_values && { allowed_values: f.allowed_values }),
          ...(f.default_value !== undefined && { default_value: f.default_value }),
          ...(f.placeholder && { placeholder: f.placeholder })
        }))
      });
    } else {
      // Regular field
      result.push({
        name: fieldName,
        display_name: field.display_name || fieldName,
        field_type: normalizeFieldType(field.field_type || field.type, 'ai'),
        is_required: field.is_required !== undefined ? !!field.is_required : !!field.required,
        is_array: !!field.is_array,
        description: field.description || field.help_text || '',
        help_text: field.description || field.help_text || '',
        ...(field.array_item_type && { array_item_type: normalizeFieldType(field.array_item_type, 'ai') }),
        ...(field.reference_object_id && { reference_object_id: field.reference_object_id }),
        ...(field.allowed_values && { allowed_values: field.allowed_values }),
        ...(field.default_value !== undefined && { default_value: field.default_value }),
        ...(field.placeholder && { placeholder: field.placeholder })
      });
    }
  });
  
  // Convert nested groups into json fields with nested_fields
  nestedGroups.forEach((nestedFields, parentName) => {
    result.push({
      name: parentName,
      display_name: parentName.split('_').map(w => 
        w.charAt(0).toUpperCase() + w.slice(1)
      ).join(' '),
      field_type: 'json',
      is_required: nestedFields.every(f => f.is_required),
      is_array: false,
      description: `${parentName} information`,
      help_text: `${parentName} information`,
      nested_fields: convertFieldsForBackend(nestedFields) // Recursive for deeper nesting
    });
  });
  
  return result;
}

// Alias for clarity - this is the function to use when saving to backend
export const prepareFieldsForBackendSave = convertFieldsForBackend;
